import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { X, Bell, Check, CheckCheck } from "lucide-react";
import PushToggle from "@/components/PushToggle";

export default function NotificationPanel({ isOpen, onClose, variant = "light" }) {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/notifications");
      setNotifications(response.data);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await apiClient.patch(`/notifications/${id}/read`);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.post("/notifications/read-all");
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark all as read");
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (!isOpen) return null;

  const isDark = variant === "dark";

  return (
    <div className="fixed inset-0 z-50" data-testid="notification-panel">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className={`absolute right-0 top-0 h-full w-full max-w-md ${
        isDark ? "bg-slate-900" : "bg-white"
      } shadow-2xl`}>
        {/* Header */}
        <div className={`p-4 border-b ${isDark ? "border-slate-800" : "border-slate-200"}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell className={`w-5 h-5 ${isDark ? "text-lime-400" : "text-indigo-900"}`} />
              <h2 className={`font-heading font-bold text-lg ${isDark ? "text-white" : "text-slate-900"}`}>
                Notifications
              </h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className={isDark ? "text-slate-400 hover:text-white" : ""}
              data-testid="close-notifications-button"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="mb-3"><PushToggle variant={isDark ? "dark" : "light"} /></div>
          
          {notifications.some(n => !n.is_read) && (
            <Button
              variant="outline"
              size="sm"
              onClick={markAllAsRead}
              className={`w-full ${isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : ""}`}
              data-testid="mark-all-read-button"
            >
              <CheckCheck className="w-4 h-4 mr-2" /> Mark all as read
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <ScrollArea className="h-[calc(100vh-120px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className={`animate-spin rounded-full h-6 w-6 border-b-2 ${
                isDark ? "border-lime-400" : "border-indigo-900"
              }`}></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className={`w-12 h-12 mx-auto mb-4 ${isDark ? "text-slate-700" : "text-slate-300"}`} />
              <p className={isDark ? "text-slate-500" : "text-slate-500"}>No notifications yet</p>
            </div>
          ) : (
            <div className="p-2">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 rounded-lg mb-2 cursor-pointer transition-colors ${
                    notification.is_read 
                      ? isDark ? "bg-slate-800/50" : "bg-slate-50" 
                      : isDark ? "bg-slate-800 border border-lime-400/20" : "bg-indigo-50 border border-indigo-100"
                  }`}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={`font-semibold text-sm ${
                        notification.is_read
                          ? isDark ? "text-slate-400" : "text-slate-600"
                          : isDark ? "text-white" : "text-slate-900"
                      }`}>
                        {notification.title}
                      </p>
                      <p className={`text-sm mt-1 ${
                        isDark ? "text-slate-400" : "text-slate-600"
                      }`}>
                        {notification.message}
                      </p>
                      <p className={`text-xs mt-2 ${
                        isDark ? "text-slate-500" : "text-slate-400"
                      }`}>
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${
                        isDark ? "bg-lime-400" : "bg-indigo-500"
                      }`} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
